import React from "react";
import type { DepGraph, ExistingWorkCode } from "../../../../../models";
import WorkCodeField from "../../../../shared/WorkCodeField/WorkCodeField";
import { swatchHex } from "../../../../../utils/color";
import { type DepCandidate } from "../../DepChipTypeahead/DepChipTypeahead";
import { activityLabel, type RowLayoutEntry } from "../../graphRows";
import PickedChip from "./PickedChip/PickedChip";
import AddTieButton from "./AddTieButton/AddTieButton";
import "./InlineAddRow.css";

export interface InlineAddArgs {
  zone: { a: string; b: string; c: string } | null;
  code: string;
  codeName: string;
  codeColor: string;
  name: string;
  days: number;
  /** Predecessors to tie (FS) — the suggested row-above when linked, plus any
   *  picked through the cell's "+ add". */
  predIds: string[];
  /** Successors to tie (FS) — the suggested row-below when linked, plus picks. */
  succIds: string[];
  /** ROW-POSITION anchor — the activity this row was typed beneath. Until the
   *  forward pass runs (on Done), the new row renders right there with "—"
   *  dates. Independent of the tie ids. */
  positionAfterId: string | null;
}

export interface InlineAddConfig {
  scheduleId: number;
  extraCodes: ExistingWorkCode[];
  /** Stage one new activity; returns its draft id — the next row's anchor. */
  onCommit: (args: InlineAddArgs) => string;
  /** Display name for a just-staged activity the graph doesn't carry yet. */
  localNameFor?: (draftId: string) => string | null;
}


type GhostEntry = Extract<RowLayoutEntry, { kind: "ghost" }>;

interface Props {
  entry: GhostEntry;
  graph: DepGraph;
  config: InlineAddConfig;
  /** Linkable targets for the pred/succ "+ add" cells — the same candidate
   *  list the normal rows use. */
  candidates: DepCandidate[];
  canSelect: boolean;
  canDelete: boolean;
  /** Open straight into edit mode (a mid-table insert). */
  autoEdit?: boolean;
  onAutoEditConsumed?: () => void;
  /** When set, ✕/Esc removes the row entirely (a transient mid-table insert)
   *  instead of collapsing back to the idle "+ add activity" ghost. */
  onDismiss?: () => void;
  /** Fired after each commit with the new draft id (the insert row uses it to
   *  advance its position below the row it just made). */
  onCommitted?: (draftId: string) => void;
  /** Editing session signal — while true the host HOLDS the forward pass;
   *  Done/Cancel flips it false and the schedule recalculates once. */
  onEditingChange?: (editing: boolean) => void;
  attachRowRef: (el: HTMLTableRowElement | null) => void;
}

/** The "+ add activity" row at the bottom of each level group (and the
 *  insert-below editor). In edit state it's a real table row — code chip ·
 *  name · days under their columns, suggested pred (the row above) and
 *  suggested successor (the row below) as toggleable chips, plus the standard
 *  "+ add" tie pickers. Enter commits and keeps the row open; ✓ Done finishes
 *  and lets the schedule recalculate. */
const InlineAddRow: React.FC<Props> = ({
  entry,
  graph,
  config,
  candidates,
  canSelect,
  canDelete,
  autoEdit = false,
  onAutoEditConsumed,
  onDismiss,
  onCommitted,
  onEditingChange,
  attachRowRef,
}) => {
  const [editing, setEditing] = React.useState(false);
  // Signal the editing session up (and clear it if this row unmounts mid-edit).
  const editingChangeRef = React.useRef(onEditingChange);
  editingChangeRef.current = onEditingChange;
  React.useEffect(() => {
    editingChangeRef.current?.(editing);
    return () => {
      if (editing) editingChangeRef.current?.(false);
    };
  }, [editing]);

  const [wc, setWc] = React.useState({ code: "", name: "", color: "" });
  const [name, setName] = React.useState("");
  const [nameTouched, setNameTouched] = React.useState(false);
  const [days, setDays] = React.useState("3");
  const [extraPreds, setExtraPreds] = React.useState<string[]>([]);
  const [extraSuccs, setExtraSuccs] = React.useState<string[]>([]);
  // The last activity added FROM THIS ROW this session — the chain anchor for
  // the next one (it may not be in the graph yet, mid-held-pass).
  const lastAddedRef = React.useRef<string | null>(null);
  const nameInputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (autoEdit) {
      setEditing(true);
      onAutoEditConsumed?.();
    }
  }, [autoEdit, onAutoEditConsumed]);

  const zoneSuffix = entry.zone
    ? [entry.zone.b, entry.zone.c].filter(Boolean).join(" - ")
    : "";
  const suggestion = wc.name ? (zoneSuffix ? `${wc.name} | ${zoneSuffix}` : wc.name) : "";
  const displayName = nameTouched ? name : suggestion;

  const labelFor = React.useCallback(
    (id: string): string => {
      const node = graph.activities[id];
      if (node) return activityLabel(node);
      return config.localNameFor?.(id) ?? "the one just added";
    },
    [graph, config],
  );
  const candidateById = React.useMemo(() => {
    const m = new Map<string, DepCandidate>();
    for (const c of candidates) m.set(c.instanceId, c);
    return m;
  }, [candidates]);

  // Where the new row sits (and which level/leaf it inherits) — NOT an automatic
  // tie. Ties are added explicitly through the "+ add" pickers below.
  const anchorId = lastAddedRef.current ?? entry.lastActivityId;

  const daysNum = Math.max(1, Math.round(Number(days)) || 0);
  const canCommit = (displayName.trim() || wc.code.trim()) && daysNum >= 1;

  const commit = () => {
    if (!canCommit) return;
    const predIds = [...extraPreds];
    const succIds = [...extraSuccs];
    const draftId = config.onCommit({
      zone: entry.zone,
      code: wc.code.trim(),
      codeName: wc.name.trim() || wc.code.trim(),
      codeColor: wc.code ? swatchHex(wc.color, wc.code) : "",
      name: displayName.trim() || wc.code.trim(),
      days: daysNum,
      predIds,
      succIds,
      positionAfterId: anchorId,
    });
    lastAddedRef.current = draftId;
    onCommitted?.(draftId);
    // Stay open for the next one — fresh code/name/picks, same days.
    setWc({ code: "", name: "", color: "" });
    setName("");
    setNameTouched(false);
    setExtraPreds([]);
    setExtraSuccs([]);
    nameInputRef.current?.focus();
  };

  // Stays open until ✓ Done / ✕ / Esc — clicking elsewhere (e.g. to pan the
  // board) must NOT discard an in-progress add.
  const close = onDismiss ?? (() => setEditing(false));

  const totalCols = 6 + (canSelect ? 1 : 0) + (canDelete ? 1 : 0);

  if (!editing) {
    return (
      <tr ref={attachRowRef} className="dep-table-ghost-row">
        <td colSpan={totalCols}>
          <button
            type="button"
            className="dep-table-ghost-btn"
            onClick={() => setEditing(true)}
          >
            ＋ add activity
            {entry.zone && (
              <span className="dep-table-ghost-zone">
                to {[entry.zone.b, entry.zone.c].filter(Boolean).join(" · ") || "this level"}
              </span>
            )}
          </button>
        </td>
      </tr>
    );
  }

  const keyHandler = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") commit();
    else if (e.key === "Escape") close();
  };

  const pickedSet = new Set([...extraPreds, ...extraSuccs]);

  return (
    <tr ref={attachRowRef} className="dep-table-ghost-row dep-table-ghost-row--editing">
      {canSelect && <td className="dep-table-col-select" />}
      <td className="dep-table-col-name">
        <div className="dep-table-ghost-edit">
          <WorkCodeField
            scheduleId={config.scheduleId}
            code={wc.code}
            name={wc.name}
            color={wc.color}
            onChange={(p) =>
              setWc((prev) => ({
                code: p.code !== undefined ? p.code : prev.code,
                name: p.name !== undefined ? p.name : prev.name,
                color: p.color !== undefined ? p.color : prev.color,
              }))
            }
            extraCodes={config.extraCodes}
          />
          <input
            ref={nameInputRef}
            className="dep-table-ghost-name"
            placeholder={suggestion || "Activity name…"}
            title={displayName || undefined}
            value={displayName}
            onChange={(e) => {
              const v = e.target.value;
              if (v) {
                setName(v);
                setNameTouched(true);
              } else {
                setName("");
                setNameTouched(false);
              }
            }}
            onKeyDown={keyHandler}
          />
        </div>
      </td>
      <td className="dep-table-col-date">
        <span className="dep-table-date">—</span>
      </td>
      <td className="dep-table-col-date">
        <span className="dep-table-date">—</span>
      </td>
      <td className="dep-table-col-dur">
        <input
          className="dep-table-ghost-days"
          type="number"
          min={1}
          value={days}
          title="Duration (workdays)"
          onChange={(e) => setDays(e.target.value)}
          onKeyDown={keyHandler}
        />
      </td>
      <td className="dep-table-col-chips">
        <div className="dep-table-chip-cell">
          {extraPreds.map((id) => (
            <PickedChip
              key={id}
              label={candidateById.get(id)?.displayName ?? labelFor(id)}
              code={candidateById.get(id)?.workCode ?? ""}
              color={candidateById.get(id)?.color ?? ""}
              onRemove={() => setExtraPreds((p) => p.filter((x) => x !== id))}
            />
          ))}
          <AddTieButton
            candidates={candidates}
            exclude={pickedSet}
            onPick={(id) => setExtraPreds((p) => [...p, id])}
          />
        </div>
      </td>
      <td className="dep-table-col-chips">
        <div className="dep-table-chip-cell">
          {extraSuccs.map((id) => (
            <PickedChip
              key={id}
              label={candidateById.get(id)?.displayName ?? labelFor(id)}
              code={candidateById.get(id)?.workCode ?? ""}
              color={candidateById.get(id)?.color ?? ""}
              onRemove={() => setExtraSuccs((p) => p.filter((x) => x !== id))}
            />
          ))}
          <AddTieButton
            candidates={candidates}
            exclude={pickedSet}
            onPick={(id) => setExtraSuccs((p) => [...p, id])}
          />
        </div>
      </td>
      {canDelete && (
        <td className="dep-table-col-actions">
          <div className="dep-table-ghost-actions">
            <button
              type="button"
              className="dep-table-ghost-commit"
              title={
                canCommit
                  ? "Add it and recalculate the schedule. Enter adds and keeps typing the next one."
                  : "Finish — recalculate the schedule"
              }
              onClick={() => {
                if (canCommit) commit();
                close();
              }}
            >
              ✓
            </button>
            <button
              type="button"
              className="dep-table-ghost-cancel"
              title="Discard this row (Esc) — already-added ones stay"
              onClick={close}
            >
              ✕
            </button>
          </div>
        </td>
      )}
    </tr>
  );
};

export default InlineAddRow;
